import React, { useEffect, useState } from 'react';
import { statsAPI } from '../services/api';
import { useAuthStore } from '../hooks/useAuthStore';
import {
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface CostStats {
  today: number;
  this_month: number;
  all_time: number;
  by_engine: Record<string, number>;
  by_model: Record<string, number>;
}

interface UsageStats {
  total_chats: number;
  total_queries: number;
  queries_by_mode: Record<string, number>;
  active_chats?: number;
  active_queries?: number;
}

export default function Stats() {
  const { user } = useAuthStore();
  const [costs, setCosts] = useState<CostStats | null>(null);
  const [allCosts, setAllCosts] = useState<CostStats | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const promises: Promise<any>[] = [
        statsAPI.getCosts(),
        statsAPI.getUsage(),
      ];
      if (user?.is_admin) {
        promises.push(statsAPI.getAllCosts());
      }

      const results = await Promise.all(promises);
      setCosts(results[0].data);
      setUsage(results[1].data);
      if (user?.is_admin && results[2]) {
        setAllCosts(results[2].data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-surface-100 mb-6">Statistics</h1>

        {/* All Users summary (admin only) */}
        {allCosts && (
          <>
            <h2 className="text-lg font-semibold text-surface-200 mb-3">All Users</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CurrencyDollarIcon className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-sm text-surface-400">Today</span>
                </div>
                <p className="text-2xl font-bold text-surface-100">
                  ${allCosts.today.toFixed(4)}
                </p>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <ClockIcon className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-surface-400">This Month</span>
                </div>
                <p className="text-2xl font-bold text-surface-100">
                  ${allCosts.this_month.toFixed(2)}
                </p>
              </div>

              <div className="card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <CurrencyDollarIcon className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm text-surface-400">All-Time</span>
                </div>
                <p className="text-2xl font-bold text-surface-100">
                  ${allCosts.all_time.toFixed(2)}
                </p>
              </div>
            </div>

            <h2 className="text-lg font-semibold text-surface-200 mb-3">Your Stats</h2>
          </>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CurrencyDollarIcon className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-sm text-surface-400">Today</span>
            </div>
            <p className="text-2xl font-bold text-surface-100">
              ${costs?.today.toFixed(4) || '0.00'}
            </p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <ClockIcon className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-sm text-surface-400">This Month</span>
            </div>
            <p className="text-2xl font-bold text-surface-100">
              ${costs?.this_month.toFixed(2) || '0.00'}
            </p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-sm text-surface-400">Total Queries</span>
            </div>
            <p className="text-2xl font-bold text-surface-100">
              {usage?.total_queries || 0}
            </p>
          </div>
        </div>

        {/* Cost by engine */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-surface-100 mb-4">
              Cost by Engine
            </h2>
            {costs?.by_engine && Object.keys(costs.by_engine).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(costs.by_engine)
                  .sort((a, b) => b[1] - a[1])
                  .map(([engine, cost]) => (
                    <div
                      key={engine}
                      className="flex items-center justify-between"
                    >
                      <span className="text-surface-300 capitalize">
                        {engine}
                      </span>
                      <span className="text-surface-100 font-medium">
                        ${cost.toFixed(4)}
                      </span>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-surface-400 text-sm">No data yet</p>
            )}
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold text-surface-100 mb-4">
              Cost by Model
            </h2>
            {costs?.by_model && Object.keys(costs.by_model).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(costs.by_model)
                  .sort((a, b) => b[1] - a[1])
                  .map(([model, cost]) => (
                    <div
                      key={model}
                      className="flex items-center justify-between"
                    >
                      <span className="text-surface-300 text-sm truncate mr-2">
                        {model}
                      </span>
                      <span className="text-surface-100 font-medium">
                        ${cost.toFixed(4)}
                      </span>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-surface-400 text-sm">No data yet</p>
            )}
          </div>
        </div>

        {/* Usage stats */}
        <div className="card mt-6">
          <h2 className="text-lg font-semibold text-surface-100 mb-4">
            Usage Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-surface-400 text-sm">All-Time Chats</p>
              <p className="text-xl font-bold text-surface-100">
                {usage?.total_chats || 0}
              </p>
              {usage?.active_chats !== undefined && (
                <p className="text-xs text-surface-500 mt-1">
                  {usage.active_chats} active
                </p>
              )}
            </div>
            <div>
              <p className="text-surface-400 text-sm">All-Time Queries</p>
              <p className="text-xl font-bold text-surface-100">
                {usage?.total_queries || 0}
              </p>
              {usage?.active_queries !== undefined && (
                <p className="text-xs text-surface-500 mt-1">
                  {usage.active_queries} active
                </p>
              )}
            </div>
            <div>
              <p className="text-surface-400 text-sm">Quick Queries</p>
              <p className="text-xl font-bold text-surface-100">
                {usage?.queries_by_mode?.quick || 0}
              </p>
            </div>
            <div>
              <p className="text-surface-400 text-sm">Deliberation Queries</p>
              <p className="text-xl font-bold text-surface-100">
                {usage?.queries_by_mode?.deliberation || 0}
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-surface-700">
            <h3 className="text-sm font-medium text-surface-400 mb-2">
              All-Time Cost
            </h3>
            <p className="text-3xl font-bold text-primary-400">
              ${costs?.all_time.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
